/**
 * Hooks des certificats (INV-L6, docs/12 §5).
 *
 * Deux publics, et c'est ce qui gouverne le fichier : `useMyCertificates` sert
 * l'élève connecté, `useCertificateCheck` sert **un visiteur sans compte**. Les
 * deux ne partagent ni route, ni type, ni clé de cache — et surtout pas de
 * fonction commune qui déciderait à l'exécution de ce qu'elle rend.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { learningKeys } from "@/shared/api/query-keys";

export type Certificate = components["schemas"]["CertificateResponse"];
export type CertificateCheck = components["schemas"]["CertificateCheckResponse"];

/** « Mes attestations » — révoquées comprises, marquées comme telles. */
export function useMyCertificates(): UseQueryResult<Certificate[], ApiError> {
  return useQuery({
    queryKey: learningKeys.certificates(),
    queryFn: () => unwrap(api.GET("/api/v1/me/certificates", {})),
  });
}

/**
 * La vérification publique d'un numéro de série.
 *
 * `retry: false` : un numéro inconnu répond 404, qui est ici une **réponse** et
 * non une panne. Réessayer trois fois ferait attendre celui qui a simplement
 * fait une faute de frappe, et multiplierait par trois les requêtes sur une
 * route ouverte à tout internet.
 */
export function useCertificateCheck(serial: string): UseQueryResult<CertificateCheck, ApiError> {
  return useQuery({
    queryKey: learningKeys.certificateCheck(serial),
    enabled: serial.length > 0,
    retry: false,
    queryFn: () =>
      unwrap(api.GET("/api/v1/certificates/verify/{serial}", { params: { path: { serial } } })),
  });
}

/** L'émission manuelle, par l'enseignant du cours (`learning.certificate.issue`). */
export function useIssueCertificate(): UseMutationResult<Certificate, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enrollmentId: string) =>
      unwrap(
        api.POST("/api/v1/enrollments/{enrollment_id}/certificate", {
          params: { path: { enrollment_id: enrollmentId } },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: learningKeys.all });
    },
  });
}

interface RevokeInput {
  certificateId: string;
  reason: string;
}

export function useRevokeCertificate(): UseMutationResult<Certificate, ApiError, RevokeInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ certificateId, reason }: RevokeInput) =>
      unwrap(
        api.DELETE("/api/v1/certificates/{certificate_id}", {
          params: { path: { certificate_id: certificateId } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: learningKeys.all });
    },
  });
}

/**
 * Ouvre le PDF dans un onglet.
 *
 * **Pas un `<a href>`**, contrairement aux pièces jointes de leçon : cette route
 * répond avec les octets du document et exige l'en-tête `Authorization`, qu'une
 * navigation du navigateur n'enverrait pas. On passe donc par le client de
 * l'application — qui ajoute le jeton et rejoue après rotation — et on ouvre le
 * résultat par une URL d'objet.
 *
 * L'URL est **révoquée** ensuite : un blob non libéré retient son contenu en
 * mémoire pour la durée de vie du document, et un élève qui consulte ses dix
 * attestations en garderait dix.
 */
export async function openCertificatePdf(certificateId: string): Promise<void> {
  const result = await api.GET("/api/v1/certificates/{certificate_id}/pdf", {
    params: { path: { certificate_id: certificateId } },
    parseAs: "blob",
  });
  const blob = await unwrap(
    Promise.resolve(result as unknown as { data?: Blob; response: Response }),
  );
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  // Laissé le temps que l'onglet s'en saisisse : révoquer immédiatement
  // annulerait l'ouverture dans certains navigateurs.
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
}
